import { useEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "@/lib/cn";

interface AnchoredMenuProps {
  /** Renders the trigger. Receives the toggle handler + current open state. */
  trigger: (args: { open: boolean; toggle: () => void }) => ReactNode;
  /** Renders the panel body. Receives a `close()` callback for menu items. */
  children: (args: { close: () => void }) => ReactNode;
  /** Horizontal anchor: "end" → right-aligned, opens leftward (default). */
  align?: "start" | "end";
  /** Extra classes for the panel (width / padding / etc.). */
  className?: string;
  /** aria-label for the panel. */
  menuLabel?: string;
  /** ARIA role for the panel (default "menu"). */
  role?: "menu" | "dialog";
}

/**
 * ÉTAP-7 — zoom-safe anchored dropdown. Replaces Radix Popper for the topbar
 * menus (UserMenu, NotificationBell).
 *
 * Why not Radix: Radix portals its content to <body> and positions it with a
 * JS-computed `transform: translate(Xpx, Ypx)` derived from getBoundingClientRect.
 * Under the ÉTAP-7 `body { zoom: 1.1 }` (globals.css:12) that fixed-position
 * portal lives inside the zoomed subtree, so the browser multiplies the translate
 * by the zoom factor → the menu is pushed ~10% of its X-offset past the viewport
 * (right-edge clip for `align="end"` topbar menus). getBoundingClientRect returns
 * zoomed coords while Radix's available-width is unzoomed → coordinate-space
 * mismatch, then the fixed-portal zoom-multiply on top.
 *
 * This positions the panel with pure CSS (`absolute right-0 top-full`) inside the
 * in-flow zoomed subtree: it both scales (correct 1.1 size) and anchors (right
 * edge to the trigger) correctly at any zoom — the same pattern as the
 * already-correct CreateMenu. No portal, no getBoundingClientRect.
 */
export function AnchoredMenu({
  trigger,
  children,
  align = "end",
  className,
  menuLabel,
  role = "menu",
}: AnchoredMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const close = () => setOpen(false);

  // Dismiss on outside-click / Escape (mirrors CreateMenu).
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      {trigger({ open, toggle: () => setOpen((v) => !v) })}
      {open && (
        <div
          role={role}
          aria-label={menuLabel}
          className={cn(
            "absolute top-full z-50 mt-2 rounded-md border bg-popover text-popover-foreground shadow-md",
            align === "end" ? "right-0" : "left-0",
            className,
          )}
        >
          {children({ close })}
        </div>
      )}
    </div>
  );
}
